import type { OptionWithOptionalProperties } from '@myosh/odin-components';
import { isObject } from '@myosh/odin-components';
import type { ValueTextType } from '../@types/common';

/**
 * Forcibly asserts an item to a particular type; sometimes when a type needs to be asserted as another type TypeScript
 * will make the user first assert the item as unknown and then to the actual type. This function is a descriptive way
 * to do that.
 * @param item - The item to force to another type.
 * @returns {T} The final type assertion.
 */
export const forceAssert = <T>(item: unknown): T => {
  return item as T;
};

export function isInstanceOfValueTextType(object: unknown): object is ValueTextType {
  return (
    isObject(object) &&
    (typeof object.value === 'number' || typeof object.value === 'string') &&
    typeof object.text === 'string'
  );
}

export function isInstanceOfOptionBuilder(object: unknown): object is OptionWithOptionalProperties {
  return (
    isObject(object) &&
    (typeof object.value === 'number' || typeof object.value === 'string') &&
    typeof object.text === 'string' &&
    typeof object.position === 'number' &&
    typeof object.archivedOption === 'boolean' &&
    typeof object.defaultOption === 'boolean'
  );
}

export function isArrayOfValueTextType(item: unknown): item is Array<ValueTextType> {
  if (!Array.isArray(item)) {
    return false;
  }
  if (item.length === 0) {
    return true; // Empty array vacuously satisfies the type constraint
  }
  return (typeof item[0].value === 'number' || typeof item[0].value === 'string') && typeof item[0].text === 'string';
}

export function isArrayOfOptionWithOptionalProperties(item: unknown): item is Array<OptionWithOptionalProperties> {
  return (
    Array.isArray(item) &&
    (typeof item[0].value === 'number' || typeof item[0].value === 'string') &&
    typeof item[0].text === 'string' &&
    typeof item[0].position === 'number' &&
    typeof item[0].archivedOption === 'boolean' &&
    typeof item[0].defaultOption === 'boolean'
  );
}

/**
 * Check if an object is empty
 * @param value object to check
 * @returns true if object has no properties
 */
export function isEmptyObject(value: Record<string, unknown>) {
  return Object.keys(value).length === 0;
}
